import React, { useMemo, useRef, useState } from "react";
import Confetti from "react-confetti";
import { X, BookOpen, Sparkles, Volume2, Cat } from "lucide-react";
import { toast } from "sonner";


const VOWELS = [
    "A", "E", "I", "O", "U",
    "OO", "EE", "AI", "OA",
];


const CONSONANTS = [
    "B", "C", "D", "F", "G", "H", "J", "K", "L", "M",
    "N", "P", "Q", "R", "S", "T", "V", "W", "X", "Y", "Z",
    "SH", "CH", "TH", "WH", "PH",
    "ING", "ER", "AR", "OR",
];


const ALL_TILES = [...VOWELS, ...CONSONANTS];


const DEFAULT_WORDS = ["CAT", "SUN", "MOON", "BAT", "SPELL"];


const SNAP_DISTANCE = 44;
const TILE_GAP = 8;
const DEFAULT_TILE_WIDTH = 62;
const TILE_HEIGHT = 56;


const SpellingSpells = ({ childId, onClose, onCoinsEarned }) => {
    const boardRef = useRef(null);


    const savedWords = JSON.parse(
        localStorage.getItem(`shadowGroveSpellingWords_${childId}`) || "null"
    );


    const savedAudio = JSON.parse(
        localStorage.getItem(`shadowGroveSpellingAudio_${childId}`) || "{}"
    );


    const savedMeta = JSON.parse(
        localStorage.getItem(`shadowGroveSpellingMeta_${childId}`) || "{}"
    );


    const guidedWords =
        savedWords?.length >= 5
            ? savedWords.map((w) => w.toUpperCase())
            : DEFAULT_WORDS;


    const isGraded = !!savedMeta?.graded;
    const trialName = "Spelling Spells";
    const completionKey = `shadowGroveSpellingCompleted_${childId}_${guidedWords.join("-")}`;
    const alreadyCompleted =
        isGraded && localStorage.getItem(completionKey) === "true";


    const [mode, setMode] = useState(null);
    const [wordIndex, setWordIndex] = useState(0);
    const [boardTiles, setBoardTiles] = useState([]);
    const [feedback, setFeedback] = useState("");
    const [sparkles, setSparkles] = useState([]);
    const [catMood, setCatMood] = useState("idle");
    const [answers, setAnswers] = useState([]);
    const [showConfetti, setShowConfetti] = useState(false);
    const [wordLocked, setWordLocked] = useState(false);


    const currentWord = guidedWords[wordIndex];


    const sortedWord = useMemo(() => {
        return boardTiles
            .slice()
            .sort((a, b) => {
                if (Math.abs(a.y - b.y) > 35) return a.y - b.y;
                return a.x - b.x;
            })
            .map((tile) => tile.letter)
            .join("");
    }, [boardTiles]);


    const estimateTileWidth = (letter) => {
        if (letter.length >= 3) return 86;
        if (letter.length === 2) return 72;
        return DEFAULT_TILE_WIDTH;
    };


    const saveCodexResult = (finalAnswers) => {
        if (!isGraded) return;


        const correctCount = finalAnswers.filter((a) => a.wasCorrect).length;
        const codexKey = `shadowGroveCodexQuizHistory_${childId}`;
        const oldHistory = JSON.parse(localStorage.getItem(codexKey) || "[]");


        const result = {
            id: Date.now().toString(),
            type: "quiz",
            game: "Spelling Spells",
            title: trialName,
            graded: true,
            score: correctCount,
            total: guidedWords.length,
            date: new Date().toISOString(),
            answers: finalAnswers,
        };


        localStorage.setItem(codexKey, JSON.stringify([result, ...oldHistory]));
        localStorage.setItem(completionKey, "true");
    };


    const addSparkleBurst = (x, y) => {
        const burst = Array.from({ length: 8 }, (_, i) => ({
            id: `${Date.now()}-${i}-${Math.random()}`,
            x: x + Math.random() * 50 - 25,
            y: y + Math.random() * 50 - 25,
        }));


        setSparkles((prev) => [...prev, ...burst]);


        setTimeout(() => {
            setSparkles((prev) =>
                prev.filter((s) => !burst.some((b) => b.id === s.id))
            );
        }, 800);
    };


    const getBoardPoint = (e) => {
        const rect = boardRef.current?.getBoundingClientRect();


        if (!rect) return { x: 20, y: 20 };


        return {
            x: Math.max(8, e.clientX - rect.left - 28),
            y: Math.max(8, e.clientY - rect.top - 28),
        };
    };


    const getMagneticPoint = (rawPoint, movingTile = null, existingTiles = boardTiles) => {
        const movingWidth = estimateTileWidth(movingTile?.letter || "");
        let bestSnap = null;


        existingTiles.forEach((tile) => {
            if (movingTile?.id && tile.id === movingTile.id) return;


            const tileWidth = estimateTileWidth(tile.letter);


            const tileLeft = tile.x;
            const tileRight = tile.x + tileWidth;
            const tileCenterY = tile.y + TILE_HEIGHT / 2;


            const movingLeft = rawPoint.x;
            const movingRight = rawPoint.x + movingWidth;
            const movingCenterY = rawPoint.y + TILE_HEIGHT / 2;


            const verticalDistance = Math.abs(movingCenterY - tileCenterY);


            if (verticalDistance > SNAP_DISTANCE) return;


            const distanceToRightSide = Math.abs(movingLeft - tileRight);
            const distanceToLeftSide = Math.abs(movingRight - tileLeft);


            if (distanceToRightSide <= SNAP_DISTANCE) {
                const candidate = {
                    x: tileRight + TILE_GAP,
                    y: tile.y,
                    distance: distanceToRightSide + verticalDistance,
                };


                if (!bestSnap || candidate.distance < bestSnap.distance) {
                    bestSnap = candidate;
                }
            }


            if (distanceToLeftSide <= SNAP_DISTANCE) {
                const candidate = {
                    x: Math.max(8, tileLeft - movingWidth - TILE_GAP),
                    y: tile.y,
                    distance: distanceToLeftSide + verticalDistance,
                };


                if (!bestSnap || candidate.distance < bestSnap.distance) {
                    bestSnap = candidate;
                }
            }
        });


        return bestSnap || rawPoint;
    };


    const addTileAtPoint = (letter, rawX, rawY) => {
        if (isGraded && wordLocked) return;


        setFeedback("");


        const newTile = {
            id: `${letter}-${Date.now()}-${Math.random()}`,
            letter,
            x: rawX,
            y: rawY,
        };


        const magneticPoint = getMagneticPoint(
            { x: rawX, y: rawY },
            newTile,
            boardTiles
        );


        const finalTile = {
            ...newTile,
            x: magneticPoint.x,
            y: magneticPoint.y,
        };


        setBoardTiles((prev) => [...prev, finalTile]);
        addSparkleBurst(finalTile.x + 28, finalTile.y + 28);
    };


    const handleBankDragStart = (e, letter) => {
        e.dataTransfer.setData(
            "application/json",
            JSON.stringify({
                type: "new",
                letter,
            })
        );
    };


    const handlePlacedDragStart = (e, tile) => {
        e.dataTransfer.setData(
            "application/json",
            JSON.stringify({
                type: "move",
                id: tile.id,
                letter: tile.letter,
            })
        );
    };


    const handleBoardDrop = (e) => {
        e.preventDefault();


        if (isGraded && wordLocked) return;


        const rawPoint = getBoardPoint(e);
        let payload = null;


        try {
            payload = JSON.parse(e.dataTransfer.getData("application/json"));
        } catch {
            const letter = e.dataTransfer.getData("letter");


            if (letter) {
                payload = {
                    type: "new",
                    letter,
                };
            }
        }


        if (!payload?.letter) return;


        if (payload.type === "move") {
            const movingTile = boardTiles.find((tile) => tile.id === payload.id);


            const magneticPoint = getMagneticPoint(
                rawPoint,
                movingTile || payload,
                boardTiles
            );


            setBoardTiles((prev) =>
                prev.map((tile) =>
                    tile.id === payload.id
                        ? {
                              ...tile,
                              x: magneticPoint.x,
                              y: magneticPoint.y,
                          }
                        : tile
                )
            );


            addSparkleBurst(magneticPoint.x + 28, magneticPoint.y + 28);
            return;
        }


        addTileAtPoint(payload.letter, rawPoint.x, rawPoint.y);
    };


    const handleTapTile = (letter) => {
        if (isGraded && wordLocked) return;


        const offset = boardTiles.length * 12;
        addTileAtPoint(letter, 20 + offset, 20 + offset);
    };


    const removeTile = (id) => {
        if (isGraded && wordLocked) return;


        setFeedback("");
        setBoardTiles((prev) => prev.filter((tile) => tile.id !== id));
    };


    const removeLastTile = () => {
        if (isGraded && wordLocked) return;


        setFeedback("");
        setBoardTiles((prev) => prev.slice(0, -1));
    };


    const clearBoard = () => {
        if (isGraded && wordLocked) return;


        setFeedback("");
        setBoardTiles([]);
    };


    const playCurrentAudio = () => {
        const currentWord = savedWords[wordIndex];

        const audio =
            savedAudio[currentWord] ||
            savedAudio[wordIndex];

        if (!audio) {
            toast.error("No voice recording for this word.");
            return;
        }

        new Audio(audio).play();
    };


    const finishTrial = (finalAnswers) => {
        saveCodexResult(finalAnswers);
        setShowConfetti(true);
        toast.success("Guided Spells complete!");


        setTimeout(() => {
            setMode("complete");
            setBoardTiles([]);
            setFeedback("");
            setWordLocked(false);
        }, 900);
    };


    const moveToNextWord = (finalAnswers) => {
        if (wordIndex + 1 >= guidedWords.length) {
            finishTrial(finalAnswers);
            return;
        }


        setTimeout(() => {
            setWordIndex((i) => i + 1);
            setBoardTiles([]);
            setFeedback("");
            setCatMood("idle");
            setWordLocked(false);
        }, 900);
    };


    const checkWord = () => {
        if (isGraded && wordLocked) return;


        const attempt = sortedWord;
        const wasCorrect = attempt === currentWord;


        const answerRecord = {
            word: currentWord,
            attempt,
            wasCorrect,
            date: new Date().toISOString(),
        };


        const finalAnswers = [...answers, answerRecord];
        setAnswers(finalAnswers);


        if (wasCorrect) {
            setFeedback("correct");
            setCatMood("happy");


            const coinsEarned = 5;
            onCoinsEarned?.(coinsEarned);


            toast.success(`Spell complete ✨ +${coinsEarned} Arcade Coins`);


            moveToNextWord(finalAnswers);
            return;
        }


        setFeedback("wrong");
        setCatMood("grumpy");


        if (isGraded) {
            setWordLocked(true);
            toast.error("Incorrect. Graded trial moves to the next word.");


            moveToNextWord(finalAnswers);
            return;
        }


        toast.error("The spell fizzled. Try again.");
    };


    const TileButton = ({ letter }) => {
        const isVowel = VOWELS.includes(letter);


        return (
            <button
                type="button"
                draggable
                onDragStart={(e) => handleBankDragStart(e, letter)}
                onClick={() => handleTapTile(letter)}
                disabled={isGraded && wordLocked}
                className={`min-w-[52px] h-12 px-3 rounded-xl font-display text-xl font-bold border transition-all hover:scale-105 cursor-grab active:cursor-grabbing disabled:opacity-50 ${
                    isVowel
                        ? "bg-[#ffd166]/20 border-[#ffd166] text-[#ffd166] shadow-[0_0_12px_rgba(255,209,102,0.25)]"
                        : "bg-[#3a86ff]/20 border-[#3a86ff] text-[#9fd0ff] shadow-[0_0_12px_rgba(58,134,255,0.2)]"
                }`}
            >
                {letter}
            </button>
        );
    };


    const BoardTile = ({ tile }) => {
        const isVowel = VOWELS.includes(tile.letter);
        const tileWidth = estimateTileWidth(tile.letter);


        return (
            <button
                type="button"
                draggable={!isGraded || !wordLocked}
                onDragStart={(e) => handlePlacedDragStart(e, tile)}
                onDoubleClick={() => removeTile(tile.id)}
                title="Drag to move. Double click to remove."
                className={`absolute h-14 px-3 rounded-xl border flex items-center justify-center font-display text-2xl cursor-grab active:cursor-grabbing transition-all ${
                    feedback === "correct" ? "animate-bounce" : ""
                } ${
                    isVowel
                        ? "bg-[#ffd166]/25 border-[#ffd166] text-[#ffd166] shadow-[0_0_18px_rgba(255,209,102,0.4)]"
                        : "bg-[#3a86ff]/25 border-[#3a86ff] text-[#9fd0ff] shadow-[0_0_18px_rgba(58,134,255,0.35)]"
                }`}
                style={{
                    left: tile.x,
                    top: tile.y,
                    minWidth: tileWidth,
                }}
            >
                {tile.letter}
            </button>
        );
    };


    const SpellBoard = ({ boardMode }) => (
        <div
            ref={boardRef}
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleBoardDrop}
            className={`relative rounded-3xl border-2 p-5 mb-5 min-h-[380px] overflow-hidden bg-[#7c3aed]/20 backdrop-blur-md shadow-[0_0_35px_rgba(255,209,102,0.25)] ${
                feedback === "wrong"
                    ? "border-red-400/70"
                    : feedback === "correct"
                    ? "border-[#ffd166] shadow-[0_0_45px_rgba(255,209,102,0.45)]"
                    : "border-[#ffd166]/70"
            }`}
        >
            {boardTiles.length === 0 && (
                <p className="text-[#e9d5ff] text-lg">
                    {boardMode === "guided"
                        ? "Drag rune tiles anywhere. Place them near each other to magnetize a word."
                        : "Build words, phrases, stories, or phonics patterns freely."}
                </p>
            )}


            {boardTiles.map((tile) => (
                <BoardTile key={tile.id} tile={tile} />
            ))}


            {sparkles.map((s) => (
                <span
                    key={s.id}
                    className="absolute pointer-events-none animate-ping text-[#ffd166]"
                    style={{
                        left: s.x,
                        top: s.y,
                    }}
                >
                    ✦
                </span>
            ))}
        </div>
    );


    return (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md flex items-center justify-center p-3">
            {showConfetti && (
                <Confetti
                    width={window.innerWidth}
                    height={window.innerHeight}
                    numberOfPieces={250}
                    recycle={false}
                />
            )}


            <div className="bb-glass rounded-3xl p-6 w-[min(96vw,980px)] max-h-[94vh] overflow-auto">
                <header className="relative mb-5 flex items-center justify-center">
                    <h2 className="font-display text-4xl text-[#ffd166] text-center">
                        Spelling Spells
                    </h2>


                    <button
                        type="button"
                        onClick={onClose}
                        className="bb-btn-ghost !p-2 absolute right-0"
                    >
                        <X size={18} />
                    </button>
                </header>


                {!mode && alreadyCompleted && (
                    <div className="text-center py-8">
                        <h3 className="font-display text-3xl text-[#ffd166]">
                            Trial Already Completed
                        </h3>


                        <p className="text-[#b0b8d6] mt-3">
                            This graded spelling trial can only be taken once.
                        </p>


                        <button
                            type="button"
                            onClick={onClose}
                            className="bb-btn-primary mt-6"
                        >
                            Close
                        </button>
                    </div>
                )}


                {!mode && !alreadyCompleted && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <button
                            type="button"
                            onClick={() => {
                                setMode("guided");
                                setBoardTiles([]);
                                setFeedback("");
                                setCatMood("idle");
                                setWordLocked(false);
                            }}
                            className="bb-card p-6 text-left hover:border-[#ffd166]/60"
                        >
                            <BookOpen className="text-[#ffd166] mb-3" />


                            <h3 className="font-display text-2xl">
                                Guided Spells
                            </h3>


                            <p className="text-sm text-[#b0b8d6] mt-2">
                                Parent-guided phonetic spell casting.
                            </p>


                            {isGraded && (
                                <p className="text-xs text-[#ffd166] mt-3">
                                    Graded Trial · One attempt per word
                                </p>
                            )}
                        </button>


                        <button
                            type="button"
                            onClick={() => {
                                setMode("sandbox");
                                setBoardTiles([]);
                                setFeedback("");
                                setCatMood("idle");
                                setWordLocked(false);
                            }}
                            className="bb-card p-6 text-left hover:border-[#3a86ff]/60"
                        >
                            <Sparkles className="text-[#3a86ff] mb-3" />


                            <h3 className="font-display text-2xl">
                                Rune Sandbox
                            </h3>


                            <p className="text-sm text-[#b0b8d6] mt-2">
                                Freely build and experiment with rune tiles.
                            </p>
                        </button>
                    </div>
                )}


                {mode === "guided" && (
                    <div>
                        <div className="mb-5 flex justify-center">
                            <div className="rounded-3xl border border-[#ffd166]/70 bg-[#7c3aed]/25 px-10 py-5 text-center shadow-[0_0_35px_rgba(255,209,102,0.35)]">
                                <h3 className="font-display text-5xl text-[#d8b4fe] drop-shadow-[0_0_14px_rgba(216,180,254,0.75)]">
                                    Guided Spells
                                </h3>


                                <p className="text-xs text-[#ffd166] mt-2">
                                    Word {wordIndex + 1}/{guidedWords.length}
                                </p>


                                {isGraded && (
                                    <p className="text-xs text-[#ffcf99] mt-2">
                                        Graded: one cast attempt only
                                    </p>
                                )}


                                <div className="mt-4 flex justify-center">
                                    <button
                                        type="button"
                                        onClick={playCurrentAudio}
                                        className="bb-btn-primary flex items-center gap-2"
                                    >
                                        <Volume2 size={16} />
                                        Hear Spell
                                    </button>
                                </div>
                            </div>
                        </div>


                        <div className="flex justify-center mb-3">
                            <div
                                className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm ${
                                    catMood === "happy"
                                        ? "bg-[#7dffb2]/20 text-[#7dffb2]"
                                        : catMood === "grumpy"
                                        ? "bg-red-400/20 text-red-300"
                                        : "bg-white/5 text-[#b0b8d6]"
                                }`}
                            >
                                <Cat size={16} />
                                {catMood === "happy"
                                    ? "The familiar dances!"
                                    : catMood === "grumpy"
                                    ? "The familiar squints at the spell..."
                                    : "The familiar watches the spell board."}
                            </div>
                        </div>


                        <SpellBoard boardMode="guided" />


                        <div className="flex justify-center gap-3 mb-5 flex-wrap">
                            <button
                                type="button"
                                onClick={removeLastTile}
                                disabled={isGraded && wordLocked}
                                className="bb-btn-ghost disabled:opacity-50"
                            >
                                Undo
                            </button>


                            <button
                                type="button"
                                onClick={clearBoard}
                                disabled={isGraded && wordLocked}
                                className="bb-btn-ghost disabled:opacity-50"
                            >
                                Clear
                            </button>


                            <button
                                type="button"
                                onClick={checkWord}
                                disabled={isGraded && wordLocked}
                                className="bb-btn-primary disabled:opacity-50"
                            >
                                Cast Spell
                            </button>
                        </div>


                        <p className="text-center text-xs text-[#6c7a9c] mb-4">
                            Tiles can go anywhere. Drop one near another to magnetize them. Double click a tile to remove it.
                        </p>
                    </div>
                )}


                {mode === "sandbox" && (
                    <div>
                        <SpellBoard boardMode="sandbox" />


                        <div className="flex justify-center gap-3 mb-5 flex-wrap">
                            <button
                                type="button"
                                onClick={removeLastTile}
                                className="bb-btn-ghost"
                            >
                                Undo
                            </button>


                            <button
                                type="button"
                                onClick={clearBoard}
                                className="bb-btn-ghost"
                            >
                                Clear Board
                            </button>
                        </div>


                        <p className="text-center text-xs text-[#6c7a9c] mb-4">
                            Use the board like a magical whiteboard: words, word families, sentences, phonics chunks, or stories.
                        </p>
                    </div>
                )}


                {mode === "complete" && (
                    <div className="text-center py-8">
                        <h3 className="font-display text-3xl text-[#ffd166]">
                            Guided Spells Complete!
                        </h3>


                        <p className="text-[#b0b8d6] mt-3">
                            All spelling words completed.
                        </p>


                        {isGraded && (
                            <p className="text-xs text-[#7dffb2] mt-3">
                                Saved to Codex.
                            </p>
                        )}


                        <button
                            type="button"
                            onClick={onClose}
                            className="bb-btn-primary mt-5"
                        >
                            Continue
                        </button>
                    </div>
                )}


                {(mode === "guided" || mode === "sandbox") && (
                    <>
                        <h4 className="font-display text-xl text-[#ffd166] mb-3">
                            Rune Tiles
                        </h4>


                        <div className="flex flex-wrap gap-2 mb-4">
                            {ALL_TILES.map((letter) => (
                                <TileButton key={letter} letter={letter} />
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};


export default SpellingSpells;
